import React from 'react';
import { Volume, VolumeX, Loader2 } from 'lucide-react';

interface AudioControlsProps {
  mode: 'muted' | 'unmuted';
  isActive: boolean;
  isLoading?: boolean;
  error: string | null;
  isSupported: boolean;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  onCycleMode: () => void;
  className?: string;
}

/**
 * Simplified Audio Controls Component
 * Mute/Unmute camera audio
 */
const AudioControls: React.FC<AudioControlsProps> = ({
  mode,
  isLoading = false,
  isActive,
  error,
  isSupported,
  size = 'md',
  showLabel = false,
  onCycleMode,
  className = ''
}) => {
  if (!isSupported) {
    return null;
  }

  const iconSize = size === 'sm' ? 16 : size === 'md' ? 20 : 24;

  // Show loading spinner
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          type="button"
          disabled
          aria-label="Initializing camera audio"
          title="Initializing audio..."
          className="inline-flex items-center justify-center border-0 p-2 rounded-full bg-black/40 backdrop-blur-sm cursor-not-allowed"
        >
          <Loader2 size={iconSize} className="animate-spin text-white" />
        </button>
      </div>
    );
  }

  const getIcon = () => {
    return mode === 'muted' ? <VolumeX size={iconSize} className="text-white" /> : <Volume size={iconSize} className="text-white" />;
  };

  const getTooltip = () => {
    return mode === 'muted' ? 'Click to unmute camera audio' : 'Click to mute audio';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        type="button"
        aria-label={mode === 'muted' ? 'Unmute camera audio' : 'Mute camera audio'}
        aria-pressed={mode === 'unmuted'}
        onClick={(e) => {
          e.stopPropagation();
          onCycleMode();
        }}
        title={getTooltip()}
        className="inline-flex items-center justify-center border-0 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-all duration-200 backdrop-blur-sm cursor-pointer"
      >
        {getIcon()}
      </button>

    </div>
  );
};

export default AudioControls;
